import { useSuspenseQuery, type MutationFunction } from "@tanstack/react-query";
import { Flex, OnboardingLayout } from "@wayflyer/flyui";
import { useIntl } from "react-intl";
import { useCustomerAppConfig } from "@wayflyer/undercarriage";

import { IncompleteStepsModal } from "../../../components/incomplete-steps-modal";

import {
  useTriggerValidation,
  useValidatedDocumentQuery,
} from "../../../queries/document-uploads/validated-documents-queries";
import {
  useFundingApplication,
  useFundingApplicationMutation,
  type FormattedFundingApplicationScreenUpdate,
} from "../../../queries/funding-application/queries";
import { UploadDocumentType } from "../../../types";
import { useDocumentNavigationActions, MESSAGES } from "../components";
import { ProfitAndLossDocumentUpload } from "../components/profit-and-loss-document-upload";
import { getAccountingThreshold } from "../../../utils/funding-journey";
import { getCustomerBusinessModelQueryOptions } from "../../../queries/customer-data/queries";

export const ProfitAndLossDocumentUploadPage = () => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: fundingApplicationData } = useFundingApplication(customerDomain);
  const { data: businessModel } = useSuspenseQuery(getCustomerBusinessModelQueryOptions(customerDomain));
  const fundingApplicationMutation = useFundingApplicationMutation(customerDomain);
  const { data: uploadedDocuments } = useValidatedDocumentQuery(customerDomain, fundingApplicationData.id);
  const { mutate: triggerValidation } = useTriggerValidation(
    customerDomain,
    fundingApplicationData.id,
    UploadDocumentType.PROFIT_AND_LOSS
  );

  const filteredDocumentTypes = uploadedDocuments?.filter(
    (doc) => doc.documentType === UploadDocumentType.PROFIT_AND_LOSS
  );
  const filteredFiles = filteredDocumentTypes?.flatMap(({ data }) => data);
  const hasUploadedDocuments = Boolean(filteredFiles?.length);

  const { primaryAction, secondaryAction, applicationSubmit, documentError } = useDocumentNavigationActions({
    forType: UploadDocumentType.PROFIT_AND_LOSS,
    onPrimaryAction: () => filteredFiles?.length && triggerValidation(filteredFiles.map(({ id }) => id)),
  });

  return (
    <>
      <IncompleteStepsModal {...applicationSubmit} />
      <OnboardingLayout
        variant="wide"
        title={intl.formatMessage(MESSAGES.profitAndLossTitle)}
        actions={{ primary: primaryAction, secondary: secondaryAction }}
      >
        <Flex maxWidth="550px">
          <ProfitAndLossDocumentUpload
            fundingApplication={fundingApplicationData}
            mutate={fundingApplicationMutation.mutate as MutationFunction<FormattedFundingApplicationScreenUpdate>}
            accountingThreshold={getAccountingThreshold(businessModel)}
            hasUploadedDocuments={hasUploadedDocuments}
            error={documentError}
          />
        </Flex>
      </OnboardingLayout>
    </>
  );
};
